import React, { useEffect, useState } from 'react';
import { parsePageInput } from '../../utils/pageJump';
import './PageJump.css';

/**
 * 需求 R117：分頁列的「輸入頁碼直接跳到該頁」控制項（四頁共用）。
 *
 * PM 2026-08-18 拍板 Q8=A：抽成共用元件、四頁 import，
 * 避免再多出第五套各自為政的分頁邏輯（四頁原本的分頁列就是各寫各的，行為全不一樣）。
 *
 * 本元件只做「加法」：呼叫端既有的 上一頁／頁碼按鈕／下一頁／每頁顯示 一律原樣保留，
 * 這裡只是插在「下一頁」右側的一個輸入框（Q2=A）。
 *
 * ── PM 拍板逐條對應 ──
 * Q1=B  只有輸入框、按 Enter 跳頁 → 沒有可點的送出按鈕。
 *        Q3 要的文案「第 [__] 頁 / 共 170 頁　前往」裡帶了「前往」兩個字，
 *       但 Q1=B 明確不要按鈕，所以「前往」在這裡是純文字提示（不可點、不是 <button>），
 *       另以 title/aria-label 告訴使用者「按 Enter」。若 PM 後來想要可點的按鈕，
 *       只需把這個 <span className="page-jump-go"> 換成 <button onClick={submit}>。
 * Q3    文案照 PM 給的字串；跳頁成功後清空輸入框（Q3=A）。
 * Q4=B  無效輸入（0／負數／大於總頁數／小數／非數字／空白）→ alert 提示且「不跳頁」，
 *       輸入框內容保留讓使用者直接修改。驗證統一走 utils/pageJump.parsePageInput，
 *       確保任何情況都不會送出 page<1 或 page>totalPages。
 * Q6=A  總頁數只有 1 頁（或還沒算出總頁數）時整個跳頁框不渲染。
 *
 * 另外（AC7）：使用者打了字卻改用 上一頁／下一頁／頁碼按鈕 換頁時，輸入框會被清空，
 * 不留下和目前頁碼不符的殘值造成誤導。
 *
 * @param {number}   page       目前頁碼
 * @param {number}   totalPages 總頁數
 * @param {Function} onJump     跳頁 callback，收到 1..totalPages 的整數
 */
const PageJump = ({ page, totalPages, onJump }) => {
  const [value, setValue] = useState('');

  // AC7：目前頁碼／總頁數一變（含使用者按上一頁、下一頁、頁碼按鈕、改每頁筆數、改篩選條件）
  // 就把尚未送出的輸入清掉。
  useEffect(() => {
    setValue('');
  }, [page, totalPages]);

  const max = Number(totalPages);
  // Q6=A：只有一頁時一併隱藏（型錄管理本來就是 totalPages > 1 才渲染整條分頁列，行為一致）。
  if (!Number.isInteger(max) || max <= 1) return null;

  const submit = () => {
    const result = parsePageInput(value, max);
    if (!result.valid) {
      // Q4=B：提示且不跳頁；不清空輸入框，使用者可以直接改。
      window.alert(result.message);
      return;
    }
    setValue('');                                   // Q3=A：跳完清空
    if (result.page !== page) onJump(result.page);  // 同一頁就不用重打一次 API
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    // AC10：中文輸入法組字中按 Enter 是「確認選字」不是「送出」，必須擋掉（比照
    // 需求 R112／ProductDetailPage.js 的既有範式：isComposing 為主、keyCode 229 為舊瀏覽器後備）。
    if (e.nativeEvent?.isComposing || e.keyCode === 229 || e.which === 229) return;
    e.preventDefault();
    submit();
  };

  const hint = `輸入 1–${max} 之間的頁碼後按 Enter 前往`;
  // 讓輸入框最多只能打到「總頁數的位數」，例如共 170 頁 → 最多 3 位；共 8492 頁 → 最多 4 位。
  // Q4=B 的合法性檢查仍在 parsePageInput 一處收斂，這裡只是把明顯不可能的長字串在輸入層擋掉，
  // 避免使用者一路打到十幾位才在 Enter 時被 alert 打斷（純輔助，不是驗證出口）。
  const maxDigits = String(max).length;

  return (
    <div className="page-jump-box">
      <span className="page-jump-label">第</span>
      <input
        type="text"
        inputMode="numeric"
        className="page-jump-input"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        maxLength={maxDigits}
        title={hint}
        aria-label={hint}
      />
      <span className="page-jump-label">頁 / 共 {max} 頁</span>
      {/* Q1=B：不是按鈕，只是文案的一部分（見檔頭說明）。加 aria-hidden 避免螢幕閱讀器把
          純文字念成「按鈕」造成誤導；提示文字已在 <input> 的 aria-label 完整說明。 */}
      <span className="page-jump-go" aria-hidden="true">前往</span>
    </div>
  );
};

export default PageJump;
